import {
  ArrowPathIcon,
  BackwardIcon,
  ClipboardDocumentIcon,
  PencilIcon,
  PlayIcon,
  TrashIcon
} from "@heroicons/react/16/solid";
import { cn } from "@moe/oss/utils/utils";
import { Character } from "@moe/priv/model/character";
import { BaseMessage, CandidateMessage, CharacterMessage as CharacterMessageI } from "@moe/priv/model/message";
import { useQuery } from "@tanstack/react-query";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { CharacterCard } from "@web/components/CharacterCard";
import { useDataContext } from "@web/route-services/chat/data-context";
import { CharacterMessageAvatar } from "@web/route-services/chat/message/CharacterMessageAvatar";
import { Display } from "@web/route-services/chat/message/Display";
import { EditingHint } from "@web/route-services/chat/message/EditingHint";
import { baseMessageEditStyles, baseMessageStyles } from "@web/route-services/chat/message/Message";
import { Switcher } from "@web/route-services/chat/message/Switcher";
import { useSwitchOnSwipe } from "@web/route-services/chat/message/use-switch-on-swipe";
import { useServiceContext } from "@web/route-services/chat/service-context";
import { ChatService } from "@web/route-services/chat/use-chat-service";
import { useAppContext } from "@web/route-services/root/AppContext";
import { AnimatePresence, motion } from "motion/react";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { OptionsButton } from "./Dropdown";

interface CharacterMessageProps {
  message: CharacterMessageI;
  isTop: boolean;
  isBottom: boolean;
  className?: string;
}

export const CharacterMessage = forwardRef<HTMLDivElement, CharacterMessageProps>(
  ({ message, isTop, isBottom, className }, ref) => {
    const { chars } = useDataContext();
    const { access } = useAppContext();
    const { service, editingMessageID, isGenerating, bottomCandidateIDX } = useServiceContext();
    const base = message[0];

    const { data: char, error: charError } = useQuery({
      queryKey: ["character", base.characterID],
      queryFn: async () => {
        const chatChar = chars.get(base.characterID);
        if (chatChar) return chatChar;
        // If the character is *not* in the chat, this is a message from a
        // character that has been removed from the chat
        // we should fetch it the character data ourselves if so.
        return await access.character.getByID(base.characterID);
      }
    });

    /**
     * If we're the bottom message, our index is managed by the chat service
     */
    const idx = useMemo(() => {
      if (isBottom) return bottomCandidateIDX ?? 0;
      return message.findIndex((m) => m.isChosen) || 0;
    }, [bottomCandidateIDX, isBottom, message]);

    const isEditing = base.id === editingMessageID;

    const chosen: BaseMessage | CandidateMessage | undefined = useMemo(() => message[idx], [idx, message]);
    const [editText, setEditText] = useState<string | undefined>(chosen?.text);

    /**
     * Always sync edit text with chosen message text
     */
    useEffect(() => {
      if (chosen?.text) setEditText(chosen.text);
    }, [chosen?.text]);

    const isSkeletonIDX = idx === message.length;
    const canRegen = !isTop && !isGenerating && !isSkeletonIDX;
    const showSwitcher = isBottom && !isEditing && message.length > 1;
    const showEditingHints = !isSkeletonIDX && isEditing && editText !== undefined;
    const showRegenArrow = isBottom && !isTop && !isEditing && !showSwitcher && !isGenerating;

    const actions = getMessageActions({
      chosen,
      base,
      service,
      isBottom,
      canRegen
    });

    const { handleTouchStart, handleTouchMove, handleTouchEnd } = useSwitchOnSwipe({
      switchLeft: () => {
        if (!isBottom) return;
        if (isEditing) return;
        service.switchLeft();
      },
      switchRight: () => {
        if (!isBottom) return;
        if (isEditing) return;
        service.switchRight();
      }
    });

    return (
      // Swipe Container
      <div
        className="flex w-full flex-col"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Message */}
        <div
          ref={ref}
          className={cn("md:max-w-3/4 text-chat-character-message-text flex shrink-0 flex-col self-start", className)}
        >
          {char && <Header isEditing={isEditing} isBottom={isBottom} char={char} baseID={base.id} />}
          {!isSkeletonIDX && editText !== undefined && (
            <div
              className={cn(
                baseMessageStyles,
                isEditing ? baseMessageEditStyles : "",
                "bg-chat-character-message-background pt-2"
              )}
            >
              <div className="flex flex-col justify-start space-y-0.5">
                <div className="text-tx-secondary flex h-fit flex-row items-center justify-end space-x-3">
                  <OptionsButton actions={actions} />
                </div>
                <div>
                  <Display
                    charName={char?.name}
                    setEditText={setEditText}
                    editText={editText}
                    message={chosen}
                    isEditing={isEditing}
                  />
                </div>
              </div>
            </div>
          )}
          {isSkeletonIDX && (
            <motion.div
              className={cn("bg-chat-character-message-background h-22 flex w-56 flex-col rounded-3xl px-6 py-4")}
              initial={{ opacity: 0.6 }}
              animate={{ opacity: [0.7, 1, 0.7] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
            >
              <div className="bg-tx-tertiary mb-2 h-[10px] w-full rounded opacity-45" />
              <div className="bg-tx-tertiary mb-2 h-[10px] w-2/3 rounded opacity-45" />
              <div className="bg-tx-tertiary mb-2 h-[10px] w-1/3 rounded opacity-45" />
            </motion.div>
          )}
          {showEditingHints && <EditingHint message={chosen} editText={editText} />}
          <div className="mt-2 flex size-8 w-full justify-center py-3 md:ml-0 md:justify-normal">
            {showSwitcher && (
              <Switcher
                len={isSkeletonIDX ? idx : message.length - 1}
                idx={idx}
                onRight={service.switchRight}
                onLeft={service.switchLeft}
              />
            )}
            {showRegenArrow && (
              <button className="size-6">
                <ArrowPathIcon
                  className="text-tx-tertiary hover:fill-button-highlight-hover active:fill-action-tertiary size-6 transition duration-300 ease-out"
                  onClick={service.switchRight}
                />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
);

interface HeaderProps {
  char: Character;
  isEditing: boolean;
  isBottom: boolean;
  baseID: number;
}

const Header = ({ isEditing, isBottom, char, baseID }: HeaderProps) => {
  const { createModal } = useAppContext();
  const { isGenerating, service } = useServiceContext();
  return (
    <div className="mb-2 ml-2 flex w-full items-end justify-between">
      <div className="flex items-end space-x-3">
        <button
          onPointerUp={() => {
            createModal({
              content: <CharacterCard char={char} />
            });
          }}
        >
          <CharacterMessageAvatar src={char?.avatar} className="cursor-pointer select-none" />
        </button>
        <p className="text-tx-primary font-semibold">{char?.name}</p>
      </div>
      <AnimatePresence mode="popLayout">
        {!isEditing && isBottom && !isGenerating && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.1 } }}
            whileTap={{ scale: 0.9 }}
            className="mr-2 h-fit w-fit px-2"
            onClick={() => {
              service.edit(baseID);
            }}
          >
            <PencilIcon className="text-tx-tertiary hover:fill-button-highlight-hover active:fill-action-tertiary size-6 cursor-pointer transition duration-300 ease-out" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

const getMessageActions = ({
  chosen,
  base,
  service,
  isBottom,
  canRegen
}: {
  chosen: BaseMessage | CandidateMessage;
  base: BaseMessage;
  service: ChatService;
  isBottom: boolean;
  canRegen: boolean;
}) => {

  return [
    {
      label: "Copy",
      icon: ClipboardDocumentIcon,
      onClick: () => {
        navigator.clipboard.writeText(chosen.text);
        toast.success("Message copied to clipboard!");
      }
    },
    {
      label: "Edit",
      icon: PencilIcon,
      onClick: () => {
        service.edit(base.id);
      }
    },
    {
      label: "Regenerate",
      icon: ArrowPathIcon,
      onClick: async () => {
        if (!isBottom) await service.rewind(base.id);
        service.regenerate(base.id);
      },
      show: canRegen
    },
    {
      label: "Rewind",
      icon: BackwardIcon,
      onClick: async () => {
        service.rewind(base.id);
      },
      show: !isBottom
    },
    {
      label: "Continue",
      icon: PlayIcon,
      onClick: async () => {
        service.continue();
      },
      show: isBottom
    },
    {
      label: "Delete",
      icon: TrashIcon,
      onClick: async () => {
        service.delete(base.id);
      }
    }
  ];
};
